/**
 * PDP Gallery — CSS scroll-based carousel with indicators + counter
 * Falls back to native scroll if Swiper doesn't initialize.
 */
(function () {
  'use strict';

  function initPdpGallery(wrapper) {
    if (!wrapper || wrapper.dataset.pdpGalleryInit === 'done') return;
    wrapper.dataset.pdpGalleryInit = 'done';

    var swiperEl = wrapper.querySelector('.featured-product-slider');
    if (!swiperEl) return;

    var swiperWrapper = swiperEl.querySelector('.swiper-wrapper');
    var allSlides = swiperEl.querySelectorAll('.swiper-slide');
    if (allSlides.length === 0) return;

    var currentIndex = 0;
    var ac = new AbortController();
    var signal = ac.signal;

    // Cleanup on section unload (Shopify theme editor)
    var editorSection = wrapper.closest('[data-shopify-editor-section]');
    if (editorSection) {
      editorSection.addEventListener('shopify:section:unload', function () {
        ac.abort();
        wrapper.dataset.pdpGalleryInit = '';
      });
    }

    // --- Make swiper-wrapper a native scroll container ---
    swiperWrapper.style.display = 'flex';
    swiperWrapper.style.overflowX = 'auto';
    swiperWrapper.style.scrollSnapType = 'x mandatory';
    swiperWrapper.style.scrollBehavior = 'smooth';
    swiperWrapper.style.webkitOverflowScrolling = 'touch';
    swiperWrapper.style.scrollbarWidth = 'none';
    swiperWrapper.style.msOverflowStyle = 'none';

    // Hide scrollbar via style injection
    var styleId = 'pdp-gallery-scrollbar-hide';
    if (!document.getElementById(styleId)) {
      var style = document.createElement('style');
      style.id = styleId;
      style.textContent = '.main-product-section .swiper-wrapper::-webkit-scrollbar, .featured-product-section .swiper-wrapper::-webkit-scrollbar { display: none; }';
      document.head.appendChild(style);
    }

    // --- Mouse drag to scroll ---
    var isDragging = false;
    var startX = 0;
    var scrollStart = 0;

    swiperWrapper.addEventListener('mousedown', function (e) {
      // Ignore right-click
      if (e.button !== 0) return;
      isDragging = true;
      startX = e.pageX;
      scrollStart = swiperWrapper.scrollLeft;
      swiperWrapper.style.scrollSnapType = 'none';
      swiperWrapper.style.scrollBehavior = 'auto';
      swiperWrapper.style.cursor = 'grabbing';
      swiperWrapper.style.userSelect = 'none';
      e.preventDefault();
    }, { signal: signal });

    document.addEventListener('mousemove', function (e) {
      if (!isDragging) return;
      var dx = e.pageX - startX;
      swiperWrapper.scrollLeft = scrollStart - dx;
    }, { signal: signal });

    document.addEventListener('mouseup', function () {
      if (!isDragging) return;
      isDragging = false;
      swiperWrapper.style.scrollSnapType = 'x mandatory';
      swiperWrapper.style.scrollBehavior = 'smooth';
      swiperWrapper.style.cursor = '';
      swiperWrapper.style.userSelect = '';
    }, { signal: signal });

    // Prevent click events on links/images after drag
    swiperWrapper.addEventListener('click', function (e) {
      if (Math.abs(swiperWrapper.scrollLeft - scrollStart) > 5) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, { capture: true, signal: signal });

    // Prevent image dragging
    swiperWrapper.querySelectorAll('img').forEach(function (img) {
      img.addEventListener('dragstart', function (e) { e.preventDefault(); }, { signal: signal });
    });

    // Filter out hidden/zero-width slides (duplicates from featured_media)
    function getVisibleSlides() {
      return Array.from(allSlides).filter(function (s) {
        return s.offsetWidth > 0;
      });
    }

    // Set slide widths based on viewport
    function isDesktop() {
      return window.innerWidth >= 992;
    }

    function updateSlideWidths() {
      var desktop = isDesktop();
      var gap = 4;
      var visibleSlides = getVisibleSlides();
      var lastVisibleSlide = visibleSlides.length > 0 ? visibleSlides[visibleSlides.length - 1] : null;
      allSlides.forEach(function (slide) {
        if (desktop) {
          slide.style.flexShrink = '0';
          slide.style.width = 'calc((100% - ' + gap + 'px) / 2)';
          // Last visible slide uses 'end' snap so it's always reachable
          slide.style.scrollSnapAlign = (slide === lastVisibleSlide) ? 'end' : 'start';
        } else {
          slide.style.flexShrink = '0';
          slide.style.width = '100%';
          slide.style.scrollSnapAlign = 'start';
        }
      });
      // Defer indicator rebuild to after layout recalculation
      requestAnimationFrame(function () {
        rebuildIndicators();
      });
    }
    updateSlideWidths();
    window.addEventListener('resize', updateSlideWidths, { signal: signal });

    // --- Scroll indicators ---
    var indicatorsContainer = wrapper.querySelector('.pdp-gallery-indicators');

    function getPageCount() {
      var slides = getVisibleSlides();
      var count = slides.length;
      if (count === 0) return 0;
      // Desktop shows 2 slides at a time, so fewer scroll positions
      if (isDesktop()) {
        return Math.ceil(count / 2);
      }
      return count;
    }

    // Use event delegation for indicator clicks
    if (indicatorsContainer) {
      indicatorsContainer.addEventListener('click', function (e) {
        var dot = e.target.closest('.indicator');
        if (!dot) return;
        var idx = parseInt(dot.dataset.index, 10);
        if (!isNaN(idx)) scrollToPage(idx);
      }, { signal: signal });
    }

    function rebuildIndicators() {
      if (!indicatorsContainer) return;
      var pageCount = getPageCount();
      var existing = indicatorsContainer.querySelectorAll('.indicator');

      // Add or remove dots to match page count
      while (existing.length > pageCount) {
        indicatorsContainer.removeChild(indicatorsContainer.lastChild);
        existing = indicatorsContainer.querySelectorAll('.indicator');
      }
      while (existing.length < pageCount) {
        var dot = document.createElement('span');
        dot.className = 'indicator';
        dot.dataset.index = existing.length;
        indicatorsContainer.appendChild(dot);
        existing = indicatorsContainer.querySelectorAll('.indicator');
      }

      // Update indices and active state
      existing.forEach(function (el, i) {
        el.dataset.index = i;
        el.classList.toggle('active', i === 0);
      });
    }

    // --- Counter (mobile) ---
    var counterEl = wrapper.querySelector('.pdp-gallery-counter-text');
    function updateCounter(slideIdx) {
      if (counterEl) {
        var slides = getVisibleSlides();
        var current = String(slideIdx + 1).padStart(2, '0');
        var total = String(slides.length).padStart(2, '0');
        counterEl.textContent = current + '/' + total;
      }
    }
    updateCounter(0);

    // --- Navigation ---
    function scrollToPage(pageIdx) {
      var slides = getVisibleSlides();
      var desktop = isDesktop();
      var pageCount = getPageCount();
      var maxScroll = swiperWrapper.scrollWidth - swiperWrapper.clientWidth;

      // Last page: scroll to the end to ensure last image is fully visible
      if (pageIdx >= pageCount - 1) {
        swiperWrapper.scrollTo({ left: maxScroll, behavior: 'smooth' });
        return;
      }

      // First page: scroll to start
      if (pageIdx <= 0) {
        swiperWrapper.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }

      // Middle pages: scroll to the slide at the page boundary
      var slideIdx = desktop ? pageIdx * 2 : pageIdx;
      if (slideIdx >= slides.length) slideIdx = slides.length - 1;
      var slide = slides[slideIdx];
      if (slide) {
        var targetLeft = slide.offsetLeft - swiperWrapper.offsetLeft;
        swiperWrapper.scrollTo({
          left: Math.min(targetLeft, maxScroll),
          behavior: 'smooth'
        });
      }
    }

    // Wire prev/next arrows (navigate by page)
    var prevBtn = wrapper.querySelector('.swiper-button-prev');
    var nextBtn = wrapper.querySelector('.swiper-button-next');
    if (prevBtn) {
      prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        currentIndex = Math.max(0, currentIndex - 1);
        scrollToPage(currentIndex);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var maxPage = getPageCount() - 1;
        currentIndex = Math.min(maxPage, currentIndex + 1);
        scrollToPage(currentIndex);
      });
    }

    // --- Track scroll position to update indicators/counter ---
    var scrollTimer;
    swiperWrapper.addEventListener('scroll', function () {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () {
        var scrollLeft = swiperWrapper.scrollLeft;
        var maxScroll = swiperWrapper.scrollWidth - swiperWrapper.clientWidth;
        var slides = getVisibleSlides();
        var desktop = isDesktop();
        var pageCount = getPageCount();

        // If at or near the end, activate the last page
        if (maxScroll > 0 && scrollLeft >= maxScroll - 5) {
          currentIndex = pageCount - 1;
          updateIndicators(currentIndex);
          updateCounter(slides.length - 1);
          return;
        }

        // If at or near the start, activate the first page
        if (scrollLeft <= 5) {
          currentIndex = 0;
          updateIndicators(0);
          updateCounter(0);
          return;
        }

        // Find closest visible slide
        var closestSlideIdx = 0;
        var closestDist = Infinity;
        slides.forEach(function (slide, idx) {
          var dist = Math.abs(slide.offsetLeft - swiperWrapper.offsetLeft - scrollLeft);
          if (dist < closestDist) {
            closestDist = dist;
            closestSlideIdx = idx;
          }
        });
        // Convert slide index to page index
        var pageIdx = desktop ? Math.floor(closestSlideIdx / 2) : closestSlideIdx;
        currentIndex = pageIdx;
        updateIndicators(pageIdx);
        updateCounter(closestSlideIdx);
      }, 50);
    }, { signal: signal });

    function updateIndicators(activeIdx) {
      if (!indicatorsContainer) return;
      var indicators = indicatorsContainer.querySelectorAll('.indicator');
      indicators.forEach(function (el, i) {
        el.classList.toggle('active', i === activeIdx);
      });
    }
  }

  function init() {
    var wrappers = document.querySelectorAll('.main-product-section .main-product-media-wrapper, .featured-product-section .main-product-media-wrapper');
    wrappers.forEach(initPdpGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // Delay slightly to let page render
    setTimeout(init, 100);
  }

  // Re-init on Shopify section load (theme editor)
  document.addEventListener('shopify:section:load', function () {
    setTimeout(init, 200);
  });
})();
